import { formatChartMonth } from "@common/lib/date-utils"
import { dateOf, isWeekend } from "./data"

export const RANGES = [
  "week",
  "two-weeks",
  "month",
  "quarter",
  "half-year",
] as const

export type Range = (typeof RANGES)[number]

const RANGE_DAYS: Record<Range, number> = {
  week: 7,
  "two-weeks": 14,
  month: 30,
  quarter: 91,
  "half-year": 182,
}

const DAY_WIDTH: Record<Range, number> = {
  week: 112,
  "two-weeks": 60,
  month: 36,
  quarter: 12,
  "half-year": 7,
}

export function rangeLabel(range: Range): string {
  switch (range) {
    case "week":
      return "Week"
    case "two-weeks":
      return "2 weeks"
    case "month":
      return "Month"
    case "quarter":
      return "Quarter"
    case "half-year":
      return "Half year"
  }
}

export function isDense(range: Range): boolean {
  return RANGE_DAYS[range] > 31
}

export function defaultStart(range: Range): number {
  return isDense(range) ? -17 : -3
}

export function rangeStep(range: Range): number {
  return range === "month" ? 28 : RANGE_DAYS[range]
}

export type TimelineDay = {
  day: number
  index: number
  date: Date
  weekend: boolean
  today: boolean
  monday: boolean
}

export type TimelineWeek = {
  index: number
  length: number
  date: Date
  offset: number
  today: boolean
}

export type TimelineMonth = { index: number; label: string }

export type Timeline = {
  first: number
  last: number
  days: TimelineDay[]
  weeks: TimelineWeek[]
  months: TimelineMonth[]
  dayWidth: number
  width: number
  dense: boolean
  weekdayLabels: boolean
}

function weeksOf(days: TimelineDay[]): TimelineWeek[] {
  const weeks: TimelineWeek[] = []
  for (const day of days) {
    const current = weeks.at(-1)
    if (!current || day.monday) {
      weeks.push({
        index: day.index,
        length: 1,
        date: day.date,
        offset: 0,
        today: day.today,
      })
    } else {
      current.length++
      if (day.today) {
        current.date = day.date
        current.offset = day.index - current.index
        current.today = true
      }
    }
  }
  return weeks
}

function monthsOf(days: TimelineDay[]): TimelineMonth[] {
  return days
    .filter((day, index) => {
      const previous = days[index - 1]
      return !previous || previous.date.getMonth() !== day.date.getMonth()
    })
    .map((day) => ({ index: day.index, label: formatChartMonth(day.date) }))
}

export function buildTimeline(range: Range, start: number): Timeline {
  const count = RANGE_DAYS[range]
  const dayWidth = DAY_WIDTH[range]
  const days = Array.from({ length: count }, (_, index) => {
    const day = start + index
    const date = dateOf(day)
    return {
      day,
      index,
      date,
      weekend: isWeekend(day),
      today: day === 0,
      monday: date.getDay() === 1,
    }
  })
  return {
    first: start,
    last: start + count - 1,
    days,
    weeks: weeksOf(days),
    months: monthsOf(days),
    dayWidth,
    width: count * dayWidth,
    dense: isDense(range),
    weekdayLabels: count < 30,
  }
}
